import { Upload, Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'

type MediaRatio = '1/1' | '3/4' | '4/3' | '16/9'

type MediaTileProps = {
  inputId: string
  title: string
  ratio: MediaRatio
  url?: string
  disabled?: boolean
  busy?: boolean
  onPickFile: (file: File) => void
  onRemove: () => void
}

function ratioClass(r: MediaRatio) {
  if (r === '1/1') return 'aspect-[1/1]'
  if (r === '3/4') return 'aspect-[3/4]'
  if (r === '4/3') return 'aspect-[4/3]'
  return 'aspect-[16/9]'
}

function acceptImage() {
  return 'image/png,image/jpeg,image/webp'
}

export function MediaTile({
  inputId,
  title,
  ratio,
  url,
  disabled = false,
  busy = false,
  onPickFile,
  onRemove,
}: MediaTileProps) {
  const hasImage = Boolean(url)
  const blocked = disabled || busy

  return (
    <div className='space-y-2'>
      <div className='flex items-center justify-between'>
        <div className='text-sm font-medium'>{title}</div>
        {busy ? (
          <div className='text-muted-foreground text-xs'>处理中…</div>
        ) : null}
      </div>

      <div
        className={cn(
          'group border-border bg-muted/20 relative w-full overflow-hidden rounded-xl border',
          ratioClass(ratio),
          blocked ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'
        )}
      >
        {hasImage ? (
          <img
            src={url}
            alt={title}
            className='h-full w-full object-cover select-none'
            draggable={false}
          />
        ) : (
          <div className='text-muted-foreground flex h-full w-full items-center justify-center text-xs'>
            未上传
          </div>
        )}

        {busy ? (
          <div className='absolute inset-0 grid place-items-center bg-black/25'>
            <div className='h-6 w-6 animate-spin rounded-full border-2 border-white/70 border-t-transparent' />
          </div>
        ) : null}

        <div
          className={cn(
            'pointer-events-none absolute inset-0 opacity-0 transition',
            !blocked && 'group-hover:opacity-100',
            hasImage ? 'bg-black/35' : 'bg-black/20'
          )}
        />

        {!blocked ? (
          <div className='absolute inset-0 flex items-center justify-center gap-3 opacity-0 transition group-hover:opacity-100'>
            <label
              htmlFor={inputId}
              className='pointer-events-auto inline-flex cursor-pointer'
              aria-label={hasImage ? '替换图片' : '上传图片'}
              title={hasImage ? '替换' : '上传'}
              onClick={(e) => e.stopPropagation()}
            >
              <span className='bg-secondary text-secondary-foreground inline-flex h-10 w-10 items-center justify-center rounded-full shadow-sm'>
                <Upload className='h-5 w-5' />
              </span>
            </label>

            {hasImage ? (
              <Button
                type='button'
                variant='secondary'
                size='icon'
                className='pointer-events-auto h-10 w-10 rounded-full'
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  onRemove()
                }}
                aria-label='删除图片'
                title='删除'
              >
                <Trash2 className='h-5 w-5' />
              </Button>
            ) : null}
          </div>
        ) : null}

        <input
          id={inputId}
          type='file'
          accept={acceptImage()}
          className='hidden'
          disabled={blocked}
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (!file) return
            onPickFile(file)
            e.currentTarget.value = ''
          }}
        />
      </div>
    </div>
  )
}
